import { useEffect, useMemo, useRef, useState } from 'react';
import {
  JALALI_MONTHS,
  WEEKDAY_LABELS,
  formatJalaliInputValue,
  getJalaliMonthGrid,
  getTodayJalali,
  gregorianIsoToJalali,
  shiftJalaliMonth,
} from '../utils/jalaliDate';

export default function JalaliDatePicker({ title, value, onChange, className = '' }) {
  const today = useMemo(() => getTodayJalali(), []);
  const selected = value ? gregorianIsoToJalali(value) : null;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState({
    year: selected?.year || today.year,
    month: selected?.month || today.month,
  });
  const rootRef = useRef(null);
  const cells = getJalaliMonthGrid(view.year, view.month);

  useEffect(() => {
    if (!selected) {
      return;
    }

    setView({ year: selected.year, month: selected.month });
  }, [selected?.year, selected?.month]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const goMonth = (delta) => {
    setView((current) => shiftJalaliMonth(current.year, current.month, delta));
  };

  return (
    <div ref={rootRef} className={`relative flex min-w-0 flex-col gap-1.5 ${className}`}>
      {title ? <p className="text-[11px] text-slate-500">{title}</p> : null}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="w-full rounded-[8px] border border-slate-200 bg-white px-2 py-1.5 text-right text-[12px] text-slate-700"
      >
        {value ? formatJalaliInputValue(value) : 'انتخاب تاریخ'}
      </button>

      {open ? (
        <div className="absolute top-full z-30 mt-1 w-[240px] rounded-[12px] border border-slate-200 bg-white p-2 shadow-lg">
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => goMonth(1)}
              className="rounded-[6px] px-2 py-1 text-[12px] text-slate-600"
              aria-label="ماه بعد"
            >
              ‹
            </button>
            <p className="text-[12px] font-medium text-slate-800">
              {JALALI_MONTHS[view.month - 1]} {view.year.toLocaleString('fa-IR')}
            </p>
            <button
              type="button"
              onClick={() => goMonth(-1)}
              className="rounded-[6px] px-2 py-1 text-[12px] text-slate-600"
              aria-label="ماه قبل"
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAY_LABELS.map((label) => (
              <span key={label} className="py-1 text-[10px] text-slate-400">
                {label}
              </span>
            ))}
            {cells.map((cell, index) => {
              if (!cell) {
                return <span key={`empty-${index}`} />;
              }

              const isSelected = value === cell.iso;
              const isToday =
                today.year === view.year && today.month === view.month && today.day === cell.day;

              return (
                <button
                  key={cell.iso}
                  type="button"
                  onClick={() => {
                    onChange(cell.iso);
                    setOpen(false);
                  }}
                  className={`h-8 rounded-[6px] text-[11px] ${
                    isSelected
                      ? 'bg-[#1FC24E] font-medium text-white'
                      : isToday
                        ? 'bg-[#1FC24E]/10 text-[#1FC24E]'
                        : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {cell.day.toLocaleString('fa-IR')}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
